import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';

function StepPill({ active, done, label }) {
  return (
    <div
      style={{
        ...styles.stepPill,
        ...(active ? styles.stepPillActive : {}),
        ...(done ? styles.stepPillDone : {}),
      }}
    >
      {label}
    </div>
  );
}

export default function DealerGroupsManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const [group, setGroup] = useState({
    name: '',
    slug: '',
    subscription_tier: 'bundle',
    default_country: 'AU',
    default_regions: 'AU',
  });

  const [sites, setSites] = useState([
    { name: '', slug: '', chat_enabled: false, default_country: 'AU' },
  ]);

  const [adminUser, setAdminUser] = useState({ email: '', full_name: '' });

  const [comms, setComms] = useState({
    enabled: true,
    region: 'AU',
    to_number: '',
    twilio_messaging_service_sid: '',
    dedicated_number: false,
  });

  const [result, setResult] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState('Test SMS from Tucana');

  const steps = useMemo(
    () => [
      { id: 'a', label: 'A. Group' },
      { id: 'b', label: 'B. Sites' },
      { id: 'c', label: 'C. Admin User' },
      { id: 'd', label: 'D. Comms' },
      { id: 'e', label: 'E. Checklist' },
    ],
    []
  );

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listDealerGroups();
      setRows(data || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to load dealer groups' });
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(0);
    setGroup({ name: '', slug: '', subscription_tier: 'bundle', default_country: 'AU', default_regions: 'AU' });
    setSites([{ name: '', slug: '', chat_enabled: false, default_country: 'AU' }]);
    setAdminUser({ email: '', full_name: '' });
    setComms({ enabled: true, region: 'AU', to_number: '', twilio_messaging_service_sid: '', dedicated_number: false });
    setResult(null);
    setTestTo('');
    setTestMsg('Test SMS from Tucana');
  };

  const openWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
  };

  const addSite = () => {
    setSites((prev) => [...prev, { name: '', slug: '', chat_enabled: false, default_country: group.default_country || 'AU' }]);
  };

  const removeSite = (idx) => {
    setSites((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateSite = (idx, patch) => {
    setSites((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const canNext = () => {
    if (step === 0) return !!group.name.trim() && !!(group.default_country || '').trim();
    if (step === 1) return sites.length >= 1 && sites.every((s) => !!s.name.trim());
    if (step === 2) return !!adminUser.email.trim();
    if (step === 3) return !comms.enabled || (!!comms.region.trim() && !!comms.to_number.trim());
    return true;
  };

  const next = () => {
    if (!canNext()) {
      setMessage({ type: 'error', text: 'Please complete required fields before continuing.' });
      return;
    }
    setMessage(null);
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const prev = () => {
    setMessage(null);
    setStep((s) => Math.max(s - 1, 0));
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: 'Copied to clipboard' });
      setTimeout(() => setMessage(null), 1500);
    } catch {
      setMessage({ type: 'error', text: 'Copy failed' });
    }
  };

  const submit = async () => {
    setBusy(true);
    setMessage(null);

    try {
      const payload = {
        name: group.name.trim(),
        slug: group.slug.trim() || undefined,
        subscription_tier: group.subscription_tier,
        default_country: (group.default_country || 'AU').trim().toUpperCase(),
        default_regions: (group.default_regions || '')
          .split(',')
          .map((x) => x.trim().toUpperCase())
          .filter(Boolean),
        sites: sites.map((s) => ({
          name: s.name.trim(),
          slug: s.slug.trim() || undefined,
          chat_enabled: !!s.chat_enabled,
          default_country: (s.default_country || group.default_country || 'AU').trim().toUpperCase(),
        })),
        admin_user: {
          email: adminUser.email.trim().toLowerCase(),
          full_name: adminUser.full_name.trim() || undefined,
        },
        comms: comms.enabled
          ? {
              region: (comms.region || group.default_country || 'AU').trim().toUpperCase(),
              to_number: comms.to_number.trim(),
              twilio_messaging_service_sid: comms.twilio_messaging_service_sid.trim() || null,
              dedicated_number: !!comms.dedicated_number,
            }
          : null,
      };

      const r = await api.createDealerGroupWizard(payload);
      setResult(r);
      setStep(4);
      await load();
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Create failed' });
    } finally {
      setBusy(false);
    }
  };

  const sendTestSms = async () => {
    if (!result?.dealer?.id) return;
    if (!testTo.trim()) {
      setMessage({ type: 'error', text: 'Enter a test destination number' });
      return;
    }

    setBusy(true);
    setMessage(null);
    try {
      const region = (comms.region || group.default_country || 'AU').trim().toUpperCase();
      const res = await api.sendDealerTestSms(result.dealer.id, {
        region,
        to_number: testTo.trim(),
        message: testMsg.trim() || 'Test SMS from Tucana',
      });
      setMessage({ type: 'success', text: `Test SMS sent. Provider id: ${res.provider_message_id || '(unknown)'}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to send test SMS' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🏢 Dealer Groups</h1>
          <p style={styles.subtitle}>Create and manage tenants (dealer groups) as Super Admin.</p>
        </div>
        <button style={styles.primaryButton} onClick={openWizard}>
          ➕ Create Dealer Group
        </button>
      </div>

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor:
              message.type === 'success'
                ? 'rgba(34, 197, 94, 0.2)'
                : 'rgba(239, 68, 68, 0.2)',
            color: message.type === 'success' ? '#86efac' : '#fca5a5',
          }}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={styles.empty}>No dealer groups yet.</div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Slug</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}># Sites</th>
                <th style={styles.th}>Comms ready</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={styles.tr}>
                  <td style={styles.td}><code style={styles.code}>{r.id}</code></td>
                  <td style={styles.td}>{r.name}</td>
                  <td style={styles.td}><code style={styles.code}>{r.slug || ''}</code></td>
                  <td style={styles.td}>{(r.created_at || '').slice(0, 10)}</td>
                  <td style={styles.td}>{r.sites_count}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(r.comms_ready ? styles.badgeGreen : styles.badgeGray) }}>
                      {r.comms_ready ? 'Ready' : 'Not ready'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {wizardOpen && (
        <div style={styles.modalOverlay} onClick={closeWizard}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Create Dealer Group</div>
                <div style={styles.modalSubtitle}>Step {step + 1} of {steps.length}</div>
              </div>
              <button style={styles.closeButton} onClick={closeWizard} disabled={busy}>
                ✕
              </button>
            </div>

            <div style={styles.stepsRow}>
              {steps.map((s, idx) => (
                <StepPill key={s.id} label={s.label} active={idx === step} done={idx < step} />
              ))}
            </div>

            {step === 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>A) Group details</h3>
                <div style={styles.grid2}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Name *</label>
                    <input style={styles.input} value={group.name} onChange={(e) => setGroup({ ...group, name: e.target.value })} placeholder="e.g. Vinnie Autos Group" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Slug (optional)</label>
                    <input style={styles.input} value={group.slug} onChange={(e) => setGroup({ ...group, slug: e.target.value })} placeholder="vinnie-autos" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Default country *</label>
                    <input style={styles.input} value={group.default_country} onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setGroup({ ...group, default_country: v });
                      setComms((c) => ({ ...c, region: v }));
                      setSites((prev) => prev.map((s) => ({ ...s, default_country: v })));
                    }} placeholder="AU" />
                    <div style={styles.help}>ISO-3166 alpha-2 (AU/US/GB).</div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Default regions (comma-separated)</label>
                    <input style={styles.input} value={group.default_regions} onChange={(e) => setGroup({ ...group, default_regions: e.target.value })} placeholder="AU, NZ" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Subscription tier</label>
                    <select style={styles.select} value={group.subscription_tier} onChange={(e) => setGroup({ ...group, subscription_tier: e.target.value })}>
                      <option value="chat_only">chat_only</option>
                      <option value="crm">crm</option>
                      <option value="autos">autos</option>
                      <option value="bundle">bundle</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>B) Sites (at least 1)</h3>
                {sites.map((s, idx) => (
                  <div key={idx} style={styles.siteCard}>
                    <div style={styles.grid2}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Site name *</label>
                        <input style={styles.input} value={s.name} onChange={(e) => updateSite(idx, { name: e.target.value })} placeholder="Downtown" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Slug (optional)</label>
                        <input style={styles.input} value={s.slug} onChange={(e) => updateSite(idx, { slug: e.target.value })} placeholder="downtown" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Default country</label>
                        <input style={styles.input} value={s.default_country} onChange={(e) => updateSite(idx, { default_country: e.target.value.toUpperCase() })} placeholder="AU" />
                      </div>
                      <label style={styles.toggle}>
                        <input type="checkbox" checked={!!s.chat_enabled} onChange={(e) => updateSite(idx, { chat_enabled: e.target.checked })} />
                        <span style={styles.toggleText}>Chat enabled</span>
                      </label>
                    </div>
                    {sites.length > 1 && (
                      <button style={styles.dangerButton} type="button" onClick={() => removeSite(idx)}>
                        Remove site
                      </button>
                    )}
                  </div>
                ))}
                <button style={styles.secondaryButton} type="button" onClick={addSite}>
                  ➕ Add another site
                </button>
              </div>
            )}

            {step === 2 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>C) Dealer Group Admin user</h3>
                <div style={styles.grid2}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email *</label>
                    <input style={styles.input} value={adminUser.email} onChange={(e) => setAdminUser({ ...adminUser, email: e.target.value })} placeholder="admin@group.com" />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Full name (optional)</label>
                    <input style={styles.input} value={adminUser.full_name} onChange={(e) => setAdminUser({ ...adminUser, full_name: e.target.value })} placeholder="Jane Doe" />
                  </div>
                </div>
                <div style={styles.help}>A temp password will be generated and shown on the final step.</div>
              </div>
            )}

            {step === 3 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>D) Comms setup shortcut</h3>
                <label style={styles.toggle}>
                  <input type="checkbox" checked={!!comms.enabled} onChange={(e) => setComms({ ...comms, enabled: e.target.checked })} />
                  <span style={styles.toggleText}>Create SMS config now</span>
                </label>

                {comms.enabled && (
                  <div style={styles.grid2}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Region *</label>
                      <input style={styles.input} value={comms.region} onChange={(e) => setComms({ ...comms, region: e.target.value.toUpperCase() })} placeholder="AU" />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Shared to_number (E.164) *</label>
                      <input style={styles.input} value={comms.to_number} onChange={(e) => setComms({ ...comms, to_number: e.target.value })} placeholder="+614xxxxxxxx" />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Twilio Messaging Service SID (optional)</label>
                      <input style={styles.input} value={comms.twilio_messaging_service_sid} onChange={(e) => setComms({ ...comms, twilio_messaging_service_sid: e.target.value })} placeholder="MGxxxxxxxx" />
                    </div>
                    <label style={styles.toggle}>
                      <input type="checkbox" checked={!!comms.dedicated_number} onChange={(e) => setComms({ ...comms, dedicated_number: e.target.checked })} />
                      <span style={styles.toggleText}>Dedicated number (bundle only)</span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>E) Post-create checklist</h3>

                {!result ? (
                  <div style={styles.help}>No result yet.</div>
                ) : (
                  <>
                    <div style={styles.kv}>
                      <div style={styles.kvRow}>
                        <div style={styles.kLabel}>Dealer Group ID</div>
                        <div style={styles.kValue}><code style={styles.code}>{result.dealer?.id}</code></div>
                        <button style={styles.secondaryButton} onClick={() => copy(result.dealer?.id || '')}>Copy</button>
                      </div>
                      <div style={styles.kvRow}>
                        <div style={styles.kLabel}>Admin email</div>
                        <div style={styles.kValue}>{adminUser.email}</div>
                      </div>
                      <div style={styles.kvRow}>
                        <div style={styles.kLabel}>Temp password</div>
                        <div style={styles.kValue}><code style={styles.code}>{result.admin_temp_password}</code></div>
                        <button style={styles.secondaryButton} onClick={() => copy(result.admin_temp_password || '')}>Copy</button>
                      </div>
                      <div style={styles.kvRow}>
                        <div style={styles.kLabel}>Login link</div>
                        <div style={styles.kValue}><code style={styles.code}>{window.location.origin}</code></div>
                        <button style={styles.secondaryButton} onClick={() => copy(window.location.origin)}>Copy</button>
                      </div>
                    </div>

                    <ul style={styles.checklist}>
                      {(result.checklist || []).map((x, i) => (
                        <li key={i} style={styles.checkItem}>{x}</li>
                      ))}
                    </ul>

                    <div style={styles.divider} />

                    <h4 style={styles.sectionTitleSmall}>Test SMS</h4>
                    <div style={styles.grid2}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>To number</label>
                        <input style={styles.input} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+614xxxxxxxx" />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Message</label>
                        <input style={styles.input} value={testMsg} onChange={(e) => setTestMsg(e.target.value)} placeholder="Test SMS" />
                      </div>
                    </div>
                    <button style={styles.primaryButton} onClick={sendTestSms} disabled={busy}>
                      {busy ? 'Sending…' : 'Send test SMS'}
                    </button>
                  </>
                )}
              </div>
            )}

            <div style={styles.modalFooter}>
              <button style={styles.secondaryButton} onClick={prev} disabled={busy || step === 0}>
                Back
              </button>

              {step < 4 ? (
                <>
                  <button style={styles.secondaryButton} onClick={closeWizard} disabled={busy}>
                    Cancel
                  </button>
                  {step === 3 ? (
                    <button style={styles.primaryButton} onClick={submit} disabled={busy || !canNext()}>
                      {busy ? 'Creating…' : 'Create Dealer Group'}
                    </button>
                  ) : (
                    <button style={styles.primaryButton} onClick={next} disabled={busy || !canNext()}>
                      Next
                    </button>
                  )}
                </>
              ) : (
                <button style={styles.primaryButton} onClick={closeWizard} disabled={busy}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: 1200 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { color: '#f1f5f9', margin: '0 0 6px 0', fontSize: 24, fontWeight: 700 },
  subtitle: { color: '#94a3b8', margin: 0, fontSize: 13 },
  message: { padding: '12px 16px', borderRadius: 10, border: '1px solid rgba(148,163,184,0.25)', marginBottom: 14 },
  loading: { color: '#94a3b8', padding: 24 },
  empty: { color: '#94a3b8', padding: 24, background: '#1e293b', border: '1px solid #334155', borderRadius: 12 },

  tableWrap: { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: 14, fontSize: 12, color: '#94a3b8', background: '#0f172a', borderBottom: '1px solid #334155', textTransform: 'uppercase', letterSpacing: 0.5 },
  tr: { borderBottom: '1px solid #334155' },
  td: { padding: 14, fontSize: 13, color: '#e2e8f0', verticalAlign: 'top' },
  code: { background: '#0b1220', border: '1px solid #1f2a44', borderRadius: 8, padding: '2px 6px', fontSize: 12, color: '#e2e8f0' },

  badge: { display: 'inline-block', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700 },
  badgeGreen: { background: 'rgba(34,197,94,0.18)', color: '#86efac', border: '1px solid rgba(34,197,94,0.35)' },
  badgeGray: { background: 'rgba(148,163,184,0.15)', color: '#cbd5e1', border: '1px solid rgba(148,163,184,0.3)' },

  primaryButton: { padding: '10px 14px', background: 'var(--brand-primary, #3b82f6)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' },
  secondaryButton: { padding: '10px 14px', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 10, fontWeight: 600, cursor: 'pointer' },
  dangerButton: { padding: '8px 12px', background: 'transparent', color: '#fca5a5', border: '1px solid #ef4444', borderRadius: 10, fontWeight: 600, cursor: 'pointer', marginTop: 10 },

  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 },
  modal: { width: '100%', maxWidth: 880, background: '#1e293b', border: '1px solid #334155', borderRadius: 16, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { color: '#f1f5f9', fontSize: 18, fontWeight: 800 },
  modalSubtitle: { color: '#94a3b8', fontSize: 12, marginTop: 2 },
  closeButton: { background: 'transparent', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer' },

  stepsRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  stepPill: { padding: '6px 10px', borderRadius: 999, background: '#0f172a', border: '1px solid #334155', color: '#94a3b8', fontSize: 12, fontWeight: 700 },
  stepPillActive: { background: 'rgba(var(--brand-primary-rgb, 59,130,246),0.18)', border: '1px solid rgba(var(--brand-primary-rgb, 59,130,246),0.4)', color: '#bfdbfe' },
  stepPillDone: { background: 'rgba(34,197,94,0.14)', border: '1px solid rgba(34,197,94,0.35)', color: '#bbf7d0' },

  section: { background: '#0f172a', border: '1px solid #334155', borderRadius: 14, padding: 14 },
  sectionTitle: { margin: 0, marginBottom: 10, color: '#f1f5f9', fontSize: 15 },
  sectionTitleSmall: { margin: '0 0 10px', color: '#f1f5f9', fontSize: 14 },

  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, alignItems: 'end' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, color: '#cbd5e1' },
  input: { background: '#020617', border: '1px solid #334155', borderRadius: 10, padding: '10px 12px', color: '#f8fafc' },
  select: { background: '#020617', border: '1px solid #334155', borderRadius: 10, padding: '10px 12px', color: '#f8fafc' },
  help: { color: '#94a3b8', fontSize: 12, lineHeight: 1.3 },

  siteCard: { background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.2)', borderRadius: 14, padding: 12, marginBottom: 10 },

  toggle: { display: 'flex', alignItems: 'center', gap: 10, background: '#020617', border: '1px solid #334155', borderRadius: 999, padding: '8px 12px', width: 'fit-content' },
  toggleText: { fontSize: 13, color: '#e2e8f0', fontWeight: 700 },

  kv: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 },
  kvRow: { display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 10, alignItems: 'center' },
  kLabel: { color: '#94a3b8', fontSize: 12 },
  kValue: { color: '#e2e8f0', fontSize: 13, overflowWrap: 'anywhere' },

  checklist: { margin: 0, paddingLeft: 18, color: '#e2e8f0', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 },
  checkItem: { lineHeight: 1.3 },

  divider: { height: 1, background: '#334155', margin: '10px 0' },

  modalFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
};
